/* eslint-disable @typescript-eslint/no-explicit-any */
import { Plus, Search } from "lucide-react";
import { ReactNode, useContext, useMemo, useState } from "react";
import DataTableComponent, {
  createTheme,
  TableColumn,
} from "react-data-table-component";
import { ThemeContext } from "../utils/ThemeContext";
import { useTranslation } from "react-i18next";
import { TableReorderModal } from "./TableReorderModal";
import { Card } from "../utils/Card";

createTheme(
  "darkMode",
  {
    text: {
      primary: "#FFFFFF", // Texte blanc
      secondary: "#AAAAAA", // Texte gris clair
    },
    background: {
      default: "rgb(17 24 39 / var(--tw-bg-opacity))", // Fond sombre
    },
    divider: {
      default: "rgba(255, 255, 255, 0.1)", // Diviseur gris foncé
    },
    highlightOnHover: {
      default: "#6a7282", // Fond gris plus clair lors du survol
      text: "#333333", // Texte blanc lors du survol
    },
    striped: {
      default: "rgb(17 24 39 / var(--tw-bg-opacity))", // Lignes alternées gris foncé
      text: "#FFFFFF", // Texte blanc pour les lignes alternées
    },
    button: {
      default: "#6b7280",
      hover: "#9ca3af",
      focus: "#fbbf24",
      disabled: "#9ca3af",
    },
    sortFocus: {
      default: "#fbbf24",
    },
  },
  "dark"
);

createTheme(
  "lightMode",
  {
    text: {
      primary: "#1F2937", // Texte gris foncé
      secondary: "#6B7280", // Texte gris moyen
    },
    background: {
      default: "#FFFFFF", // Fond blanc
    },
    divider: {
      default: "#ECECEC", // Diviseur gris clair
    },
    highlightOnHover: {
      default: "#F3F4F6", // Fond gris très clair lors du survol
      text: "#111827", // Texte presque noir lors du survol
    },
    striped: {
      default: "#FFFFFF", // Lignes alternées gris très clair
      text: "#1F2937", // Texte gris foncé pour les lignes alternées
    },
    button: {
      default: "#9CA3AF",
      hover: "#6B7280",
      focus: "#F59E0B",
      disabled: "#E5E7EB",
    },
    sortFocus: {
      default: "#F59E0B",
    },
  },
  "light"
);

interface DataTableProps {
  title: string;
  datas: any[];
  columns: TableColumn<any>[];
  filters: string[];
  actions?: ReactNode;
  onRowClicked?: (row: any) => void;
  onAdd?: () => void;
  onReorder?: (list: any[]) => void;
}

export function DataTable({
  title,
  datas: data,
  columns,
  filters,
  actions,
  onRowClicked,
  onAdd,
  onReorder,
}: DataTableProps) {
  const context = useContext(ThemeContext);
  const { darkMode } = context ?? { darkMode: false };
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");

  // Fonction de filtrage des données en fonction de la recherche
  const filteredData = useMemo(
    () =>
      searchText === ""
        ? sortDataByRank(data)
        : sortDataByRank(data).filter((item) =>
            filters.some((key: string) =>
              item[key]
                ?.toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())
            )
          ),
    [data, searchText, filters]
  );

  const translatedColumns = useMemo(
    () =>
      columns.map((column: TableColumn<any>) => ({
        ...column,
        name: typeof column.name == "string" ? t(column.name) : column.name,
      })),
    [columns, t]
  );

  return (
    <Card
      title={title}
      className="!pb-0"
      actions={
        <div className="flex gap-2 pt-3 md:pt-0">
          <div className="flex gap-2 items-center border-b border-gray-300 dark:border-gray-600">
            <Search className="w-4 h-4 text-gray-600 dark:text-gray-200" />
            <input
              type="text"
              className="p-1 w-full outline-none text-gray-600 dark:text-gray-200"
              placeholder={t("search")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          {actions}
          {onAdd && (
            <button
              onClick={onAdd}
              className="p-2 border border-gray-300 rounded-3xl hover:bg-blue-600 hover:text-white text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-blue-600"
            >
              <Plus className="w-4 h-4 " />
            </button>
          )}

          {onReorder != null && (
            <TableReorderModal
              data={sortDataByRank(data ?? [])}
              onReorder={(newList: any[]) => {
                onReorder(newList);
              }}
            />
          )}
        </div>
      }
    >
      <DataTableComponent
        columns={translatedColumns}
        data={filteredData}
        theme={darkMode ? "darkMode" : "lightMode"}
        onRowClicked={(row: any) => {
          onRowClicked?.(row);
        }}
        pagination
        paginationPerPage={50}
        paginationRowsPerPageOptions={[10, 30, 50, 100]}
        paginationComponentOptions={{
          rowsPerPageText: "",
        }}
        highlightOnHover
        striped
        noDataComponent={t("error.noDataMessage")}
        customStyles={{
          rows: {
            style: {
              cursor: "pointer",
            },
          },
          table: {
            style: {
              "& .cMpwUR": {
                // 2 maxlines, no ellipsis
                maxHeight: "4em",
                whiteSpace: "normal",
              },
            },
          },
        }}
      />
    </Card>
  );
}

function sortDataByRank(data: any[]): any[] {
  if (data.length === 0 || !("rank" in data[0])) return data;

  return data
    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
    .map((item, index) => ({
      ...item,
      rank: index + 1,
    })) as any[];
}
